<template>
  <div>
    <v-card elevation="0" class="mt-5 mb-5 mep-root" flat v-if="mitigationFarms.length > 0">
  
      <!-- Panel header -->
      <div class="mep-header px-4 pt-4 pb-3">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap:12px;">
          <div class="d-flex align-center" style="gap:8px;">
            <div class="mep-header-icon">
              <v-icon color="warning" size="22">mdi-shield-alert-outline</v-icon>
            </div>
            <div>
              <div class="font-weight-bold" style="font-size:16px; line-height:1.2;">{{ $t('dueDiligence.localDbAndEudrMitigationTitle') }}</div>
              <div class="text-caption text--secondary">{{ $t('dueDiligence.localDbAndEudrMitigationSubtitle') }}</div>
            </div>
          </div>
          <div class="d-flex align-center flex-wrap" style="gap:10px;">
            <div class="mep-stat-pill mep-stat-pill--yellow">
              <span class="mep-stat-pill__dot"></span>
              <span class="mep-stat-pill__label">Medium Risk</span>
              <span class="mep-stat-pill__count">{{ yellowFarmCount }}</span>
            </div>
            <div class="mep-stat-pill mep-stat-pill--red">
              <span class="mep-stat-pill__dot"></span>
              <span class="mep-stat-pill__label">High Risk</span>
              <span class="mep-stat-pill__count">{{ lightRedFarmCount }}</span>
            </div>
            <div class="mep-stat-pill mep-stat-pill--green">
              <span class="mep-stat-pill__label">Approved</span>
              <span class="mep-stat-pill__count">{{ approvedCount }}</span>
            </div>
          </div>
        </div>
        <!-- Review progress bar -->
        <div class="mep-progress mt-3">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="text-caption font-weight-medium mep-progress__label">Review Progress</span>
            <span class="text-caption font-weight-bold mep-progress__fraction">
              {{ approvedCount }}&thinsp;/&thinsp;{{ mitigationFarms.length }} reviewed
            </span>
          </div>
          <v-progress-linear
            :value="mitigationFarms.length > 0 ? (approvedCount / mitigationFarms.length) * 100 : 0"
            height="6"
            color="success"
            background-color="#E0E0E0"
            rounded
          />
        </div>
      </div>
  
      <!-- Risk filter bar -->
      <div class="mep-filter-bar px-4 py-3">
        <div class="d-flex align-center flex-wrap justify-space-between" style="gap:12px;">
          <div class="d-flex align-center flex-wrap" style="gap:8px;">
            <span class="mep-filter-label">Filter</span>
            <button
              type="button"
              class="mep-filter-chip"
              :class="{ 'mep-filter-chip--active': riskFilter === 'all' }"
              @click="setRiskFilter('all')"
            >
              All
              <span class="mep-filter-chip__count">{{ mitigationFarms.length }}</span>
            </button>
            <button
              type="button"
              class="mep-filter-chip mep-filter-chip--yellow"
              :class="{ 'mep-filter-chip--active': riskFilter === 'yellow' }"
              @click="setRiskFilter('yellow')"
            >
              <v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>
              Medium Risk
              <span class="mep-filter-chip__count">{{ yellowFarmCount }}</span>
            </button>
            <button
              type="button"
              class="mep-filter-chip mep-filter-chip--red"
              :class="{ 'mep-filter-chip--active': riskFilter === 'light_red' }"
              @click="setRiskFilter('light_red')"
            >
              <v-icon x-small class="mr-1">mdi-alert-octagon-outline</v-icon>
              High Risk
              <span class="mep-filter-chip__count">{{ lightRedFarmCount }}</span>
            </button>
          </div>
          <div class="text-caption text--secondary">
            Showing <strong>{{ filteredMitigationFarms.length }}</strong> of {{ mitigationFarms.length }} farm(s)
          </div>
        </div>
      </div>
  
      <v-divider />
  
      <!-- Sticky bulk action bar — appears when farms are selected -->
      <v-slide-y-transition>
        <div v-if="selectedFarms.length > 0" class="mep-bulk-bar">
          <div class="mep-bulk-bar__inner">
            <div class="d-flex align-center" style="gap:8px;">
              <v-icon size="18" :style="{ color: 'white !important' }">mdi-checkbox-marked-circle</v-icon>
              <span class="text-body-2 font-weight-bold white--text">
                {{ selectedFarms.length }} selected
              </span>
            </div>
            <div class="d-flex align-center" style="gap:8px;">
              <v-btn x-small text class="white--text" @click="selectedFarms = []">
                Clear
              </v-btn>
              <v-btn small depressed class="mep-bulk-bar__approve-btn" :loading="approveDialog.loading" @click="openBulkApproveDialog">
                <v-icon small class="mr-1">mdi-check-all</v-icon>
                Approve Selected ({{ selectedFarms.length }})
              </v-btn>
            </div>
          </div>
        </div>
      </v-slide-y-transition>

      <div class="px-4 pt-4 pb-4">
        <!-- Select All row -->
        <div class="d-flex align-center mb-3">
          <v-checkbox
            v-model="selectAll"
            :indeterminate="selectedFarms.length > 0 && selectedFarms.length < selectableFarms.length"
            color="success"
            hide-details dense
            class="mt-0 pt-0"
            :disabled="selectableFarms.length === 0"
          >
            <template v-slot:label>
              <span class="text-body-2 black--text">
                {{ selectAll ? 'Deselect All' : 'Select All' }}
                <span class="text--secondary">({{ selectableFarms.length }} eligible)</span>
              </span>
            </template>
          </v-checkbox>
        </div>
  
        <!-- Empty filter state -->
        <div v-if="filteredMitigationFarms.length === 0" class="mep-empty-filter text-center py-8">
          <v-icon size="40" color="grey lighten-1">mdi-filter-off-outline</v-icon>
          <div class="text-body-2 font-weight-medium mt-3 mb-1">No farms match this filter</div>
          <div class="text-caption text--secondary mb-4">Try another risk level or view all farms.</div>
          <v-btn small outlined color="primary" @click="setRiskFilter('all')">Show All Farms</v-btn>
        </div>
  
        <!-- Farm evidence cards -->
        <div v-for="farm in filteredMitigationFarms" :key="farm.id" class="mb-4">
          <v-card outlined class="mep-farm-card" :class="getFarmCardClass(farm)">
            <div class="mep-farm-card__accent" :class="getFarmAccentClass(farm)"></div>
            <div class="mep-farm-card__body">
              <!-- Farm header -->
              <v-card-title class="py-3 px-4 d-flex align-start" style="gap:8px; min-height:56px;">
                <v-checkbox
                  v-if="canSelectFarm(farm)"
                  :input-value="selectedFarms.includes(farm.id)"
                  @change="toggleFarm(farm.id)"
                  color="success" hide-details dense
                  class="mt-0 pt-0 flex-shrink-0"
                  style="margin-top:3px;"
                  @click.stop
                />
                <div style="min-width:0; flex:1;">
                  <div class="d-flex align-center" style="gap:6px;">
                    <span class="text-body-1 font-weight-bold">{{ farm.farm.farmName || ('Farm ' + farm.farm.id) }}</span>
                    <span class="text-caption text--secondary">
                      {{ farm.farm.farmerRegId || farm.farm.nationalFarmId || farm.farm.id }}
                    </span>
                  </div>
                  <div class="d-flex align-center flex-wrap mt-1" style="gap:6px;">
                    <v-chip x-small v-if="!isFarmApproved(farm) && isHighRiskFarm(farm)" class="mep-risk-chip mep-risk-chip--red">
                      <v-icon x-small class="mr-1">mdi-alert-octagon-outline</v-icon>
                      High Risk
                    </v-chip>
                    <v-chip x-small v-else-if="!isFarmApproved(farm)" class="mep-risk-chip mep-risk-chip--yellow">
                      <v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>
                      Medium Risk
                    </v-chip>
                    <v-chip x-small v-if="isFarmApproved(farm)" class="mep-status-chip mep-status-chip--approved">
                      <v-icon x-small class="mr-1" style="color:inherit">mdi-check-circle</v-icon> Approved
                    </v-chip>
                    <v-chip x-small v-else-if="isRevisionRequested(farm)" class="mep-status-chip mep-status-chip--revision">
                      <v-icon x-small class="mr-1" style="color:inherit">mdi-reload</v-icon> Revision Requested
                    </v-chip>
                    <v-chip x-small v-else-if="hasDocs(farm)" class="mep-status-chip mep-status-chip--pending">
                      <v-icon x-small class="mr-1" style="color:inherit">mdi-clock-outline</v-icon> Pending Review
                    </v-chip>
                    <v-chip x-small v-else class="mep-status-chip mep-status-chip--awaiting">
                      <v-icon x-small class="mr-1" style="color:inherit">mdi-timer-sand</v-icon> Awaiting Submission
                    </v-chip>
                  </div>
                </div>
              </v-card-title>
  
              <v-divider />
  
              <v-card-text class="px-4 py-3">
                <div v-if="!hasDocs(farm)" class="mep-no-docs">
                  <div class="d-flex align-center py-3 px-3">
                    <v-icon color="grey lighten-1" class="mr-3">mdi-file-hidden</v-icon>
                    <span class="text-body-2 text--secondary">No evidence uploaded yet by the supplier.</span>
                  </div>
                  <div
                    v-if="!isFarmApproved(farm) && !isRevisionRequested(farm)"
                    class="mep-action-row pb-3 px-3"
                  >
                    <v-btn
                      small
                      outlined
                      color="primary"
                      class="mep-action-row__secondary"
                      :loading="uploadLoading[farm.id]"
                      @click="requestMitigationUpload(farm)"
                    >
                      <v-icon small class="mr-1">mdi-send-outline</v-icon>
                      {{ $t('dueDiligence.requestToUploadMitigation') }}
                    </v-btn>
                    <v-btn small color="primary" dark depressed class="mep-action-row__primary" @click="openApproveDialog(farm)">
                      <v-icon small class="mr-1" style="color:white!important">mdi-check-circle</v-icon>
                      Approve Evidence
                    </v-btn>
                  </div>
                </div>
  
                <template v-else>
                  <!-- Flat document list grouped by source category -->
                  <div class="mep-docs-section">
                    <template v-for="sourceType in getSourceTypesForFarm(farm)">
                      <div
                        v-if="getDocsBySource(farm, sourceType).length > 0"
                        :key="'group-' + sourceType"
                        class="mep-docs-group"
                      >
                        <div class="mep-docs-group__header">
                          <span class="mep-docs-group__badge" :class="'mep-docs-group__badge--' + sourceType.replace('_', '-').toLowerCase()">
                            {{ getDocCountBySource(farm, sourceType) }}
                          </span>
                          <span class="mep-docs-group__label">{{ getSourceLabel(sourceType) }}</span>
                        </div>
                        <div
                          v-for="(doc, di) in getDocsBySource(farm, sourceType)"
                          :key="sourceType + '-' + (doc.id || di)"
                          class="mep-doc-row"
                        >
                          <v-icon :color="getDocIconColor(doc)" size="22" class="flex-shrink-0">{{ getDocIcon(doc) }}</v-icon>
                          <div class="mep-doc-row__info">
                            <div class="mep-doc-row__name">{{ docName(doc) }}</div>
                            <div v-if="doc.riskMitigationComment" class="mep-doc-row__comment text-truncate">
                              {{ doc.riskMitigationComment }}
                            </div>
                          </div>
                          <v-btn x-small text color="primary" class="flex-shrink-0" @click="openDoc(doc)">
                            <v-icon x-small class="mr-1">mdi-open-in-new</v-icon>
                            Open
                          </v-btn>
                        </div>
                      </div>
                    </template>
                  </div>
  
                  <v-expand-transition>
                    <div v-if="rejectingFarmId === farm.id" class="mt-3">
                      <v-textarea
                        v-model="rejectComments[farm.id]"
                        outlined dense :rows="2"
                        label="Reason for rejection"
                        hide-details class="mb-2"
                      />
                      <div class="d-flex justify-end" style="gap:8px;">
                        <v-btn :style="{fontWeight: 'bold'}" small outlined color="primary" @click="rejectingFarmId = null">
                          <v-icon small class="mr-1" style="color:primary!important">mdi-close</v-icon>
                          Cancel</v-btn>
                        <v-btn :style="{fontWeight: 'bold'}" small depressed color="error" dark :loading="rejectLoading[farm.id]" @click="confirmReject(farm)">
                          <v-icon small class="mr-1" style="color:white!important">mdi-check</v-icon>
                          Confirm Rejection
                        </v-btn>
                      </div>
                    </div>
                  </v-expand-transition>

                  <div v-if="isRevisionRequested(farm) && !isFarmApproved(farm)" class="mt-3 mep-no-docs d-flex align-center py-2 px-3">
                    <v-icon color="orange darken-1" class="mr-3">mdi-clock-outline</v-icon>
                    <span class="text-body-2 text--secondary">
                      Waiting for the cooperative to upload revised evidence.
                    </span>
                  </div>

                  <div v-if="!isFarmApproved(farm) && !isRevisionRequested(farm) && rejectingFarmId !== farm.id"
                    class="mep-action-row">
                    <v-btn small outlined color="primary" class="mep-action-row__secondary" @click="startReject(farm)">
                      <v-icon small class="mr-1">mdi-refresh</v-icon>
                      Request Revision
                    </v-btn>
                    <v-btn small color="primary" dark depressed class="mep-action-row__primary" @click="openApproveDialog(farm)">
                      <v-icon small class="mr-1" style="color:white!important">mdi-check-circle</v-icon>
                      Approve Evidence
                    </v-btn>
                  </div>
                </template>
              </v-card-text>
            </div>
          </v-card>
        </div>
      </div>
  
    </v-card>

    <!-- Approve evidence dialog -->
    <v-dialog v-model="approveDialog.open" persistent :max-width="$vuetify.breakpoint.mobile ? '95vw' : '640'">
      <v-card v-if="approveDialog.farms.length" class="mep-approve-dialog">
        <v-toolbar color="primary" elevation="0" dense>
          <v-icon color="white" :style="{ color: 'white !important' }" class="mr-2">mdi-check-circle-outline</v-icon>
          <v-toolbar-title class="white--text text-subtitle-1 font-weight-bold">
            {{ $t('dueDiligence.approveMitigationEvidenceTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="closeApproveDialog">
            <v-icon style="color: white !important">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-5 px-6 black--text">
          <p class="text-body-1 font-weight-medium mb-2">{{ approveDialogIntro }}</p>
          <p class="text-body-2 text--secondary mb-0">{{ approveDialogSummary }}</p>

          <v-divider class="my-4" />

          <div class="mb-1">
            <v-checkbox
              v-model="approveDialog.isTemporaryApproval"
              color="primary"
              class="mt-0 pt-0"
              hide-details
            >
              <template v-slot:label>
                <div class="mep-approve-dialog__checkbox-label">
                  <span class="font-weight-medium black--text">{{ $t('dueDiligence.temporaryApprovalLabel') }}</span>
                  <span class="text-caption black--text d-block mt-1">
                    {{ $t('dueDiligence.temporaryApprovalDescription') }}
                  </span>
                </div>
              </template>
            </v-checkbox>
          </div>

          <div v-if="approveDialog.isTemporaryApproval" class="mt-4 mb-2">
            <div class="d-flex align-center mb-2">
              <span class="font-weight-medium mr-2">{{ $t('dueDiligence.approvalExpirationPeriod') }}</span>
              <v-tooltip top color="primary" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="16" color="primary" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                </template>
                <span>{{ $t('dueDiligence.approvalExpirationDays') }}</span>
              </v-tooltip>
            </div>
            <v-row dense no-gutters>
              <v-col cols="4" class="pr-2">
                <v-text-field
                  v-model.number="approveDialog.approvalExpirationValue"
                  type="number"
                  min="1"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="8">
                <v-select
                  v-model="approveDialog.approvalExpirationUnit"
                  :items="expirationUnits"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                />
              </v-col>
            </v-row>
            <p class="text-caption text--secondary mt-2 mb-0">
              {{ $t('dueDiligence.approveMitigationEvidenceRevertNote') }}
            </p>
          </div>

          <div
            v-if="approveDialogHasFarmsWithoutDocs"
            class="mep-approve-dialog__warning mt-4"
          >
            <div class="mep-approve-dialog__warning-title">
              {{ $t('dueDiligence.approveMitigationNoDocumentsWarningTitle') }}
            </div>
            <div class="mep-approve-dialog__warning-text">
              {{ approveDialogNoDocsWarning }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4 pt-0">
          <v-spacer />
          <v-btn outlined color="primary" @click="closeApproveDialog">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="approveDialog.loading"
            :disabled="approveDialog.isTemporaryApproval && !isApproveExpirationValid"
            @click="confirmApprove"
          >
            <v-icon small class="mr-1" style="color: white !important">mdi-check</v-icon>
            {{ $t('dueDiligence.approveMitigationEvidenceButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
  
  <script>
  import DeforestationService from '@/_services/DeforestationService'
  import s3UploadService from '@/_services/S3UploadService'
  import {
    isMitigationReviewFarm,
    isFarmMediumRisk,
    isFarmHighRisk,
    getFarmMitigationFiles,
  } from '../../reports/mixins/trafficLightAssessmentMixin'
  import { isMitigationEvidenceApproved, getProductionPlaceDeforestationInfo } from '@/utils/trafficLightCalculator'
  import {
    getDdsUpdateFarmId,
    getProductionPlaceDeforestationStatus,
    getReportProductionPlaceId,
    isMitigationRevisionRequested,
    resolveMitigationApprovalStatus,
    throwOnGraphqlErrors,
  } from '@/utils/ddsMitigationNotification'
  import {
    DDS_MITIGATION_SOURCE_TYPE,
    filterMitigationFilesBySource,
    countMitigationFilesBySource,
    hasUntypedMitigationFiles,
  } from '@/utils/ddsMitigationSourceType'
  
  export default {
    name: 'MitigationEvidencePanel',
    props: {
      productionPlaces: { type: Array, default: () => [] },
      reportId: { type: [Number, String], required: true },
      report: { type: Object, default: null },
      organizationId: { type: [Number, String], default: null },
      supplierCfUserId: { type: [Number, String], default: null },
      reportRefNumber: { type: String, default: '' },
      approvalExpirationValue: { type: Number, default: 30 },
      approvalExpirationUnit: { type: String, default: 'days' },
    },
    data() {
      return {
        riskFilter: 'all',
        rejectingFarmId: null,
        rejectComments: {},
        uploadLoading: {},
        rejectLoading: {},
        localApproved: {},
        localRejected: {},
        selectedFarms: [],
        bulkLoading: false,
        approveDialog: {
          open: false,
          farms: [],
          isTemporaryApproval: false,
          approvalExpirationValue: 30,
          approvalExpirationUnit: 'days',
          loading: false,
        },
        farmSourceTabs: {},
        DDS_MITIGATION_SOURCE_TYPE,
      }
    },
    computed: {
      mitigationFarms() {
        return this.productionPlaces.filter(f =>
          isMitigationReviewFarm(f, { hasDocs: this.hasDocs(f) })
        )
      },
      yellowFarmCount() {
        return this.mitigationFarms.filter(f => this.isMediumRiskFarm(f)).length
      },
      lightRedFarmCount() {
        return this.mitigationFarms.filter(f => this.isHighRiskFarm(f)).length
      },
      filteredMitigationFarms() {
        if (this.riskFilter === 'yellow') {
          return this.mitigationFarms.filter(f => this.isMediumRiskFarm(f))
        }
        if (this.riskFilter === 'light_red') {
          return this.mitigationFarms.filter(f => this.isHighRiskFarm(f))
        }
        return this.mitigationFarms
      },
      approvedCount() {
        return this.mitigationFarms.filter(f => this.isFarmApproved(f)).length
      },
      pendingCount() {
        return this.mitigationFarms.filter(f => !this.isFarmApproved(f)).length
      },
      selectableFarms() {
        return this.filteredMitigationFarms.filter(f => this.canSelectFarm(f))
      },
      approveDialogHasFarmsWithoutDocs() {
        return (this.approveDialog.farms || []).some(f => !this.hasDocs(f))
      },
      approveDialogIntro() {
        const farms = this.approveDialog.farms || []
        if (farms.length === 1) {
          const name = farms[0].farm?.farmName || 'this farm'
          return this.$t('dueDiligence.approveMitigationEvidenceIntroSingle', { name })
        }
        return this.$t('dueDiligence.approveMitigationEvidenceIntroBulk', { count: farms.length })
      },
      approveDialogSummary() {
        return this.approveDialog.isTemporaryApproval
          ? this.$t('dueDiligence.approveMitigationEvidenceTemporarySummary')
          : this.$t('dueDiligence.approveMitigationEvidencePermanentSummary')
      },
      approveDialogNoDocsWarning() {
        return this.approveDialog.isTemporaryApproval
          ? this.$t('dueDiligence.approveMitigationWithoutDocumentsNote')
          : this.$t('dueDiligence.approveMitigationWithoutDocumentsPermanentNote')
      },
      expirationUnits() {
        return [
          { text: this.$t('dueDiligence.approvalExpirationDays'), value: 'days' },
          { text: this.$t('dueDiligence.approvalExpirationWeeks'), value: 'weeks' },
          { text: this.$t('dueDiligence.approvalExpirationMonths'), value: 'months' },
        ]
      },
      isApproveExpirationValid() {
        if (!this.approveDialog.isTemporaryApproval) return true
        return Number(this.approveDialog.approvalExpirationValue) > 0
      },
      selectAll: {
        get() {
          return this.selectableFarms.length > 0 && this.selectedFarms.length === this.selectableFarms.length
        },
        set(val) {
          this.selectedFarms = val ? this.selectableFarms.map(f => f.id) : []
        },
      },
    },
    watch: {
      riskFilter() {
        this.selectedFarms = this.selectedFarms.filter(id =>
          this.selectableFarms.some(f => f.id === id)
        )
      },
    },
    methods: {
      setRiskFilter(filter) {
        this.riskFilter = filter
      },
      canSelectFarm(farm) {
        return !this.isFarmApproved(farm) && !this.isRevisionRequested(farm)
      },
      async requestMitigationUpload(farm) {
        this.$set(this.uploadLoading, farm.id, true)
        try {
          const comment = 'Mitigation documentation required.'
          const reportPlaceId = getReportProductionPlaceId(farm)

          if (reportPlaceId) {
            const persistRes = await DeforestationService.productionPlaceRiskMitigation({
              id: reportPlaceId,
              eudr_deforestation_status: getProductionPlaceDeforestationStatus(farm),
              risk_mitigation_comment: `Upload requested: ${comment}`,
              files: [],
            })
            throwOnGraphqlErrors(persistRes, 'Failed to save upload request')
          }

          this.$emit('request-upload', {
            farm,
            mitigationSourceType: DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE,
          })
        } catch (e) {
          console.error(e)
          this.$notify({
            text: e?.message || 'Failed to queue upload request. Please try again.',
            type: 'error',
          })
        } finally {
          this.$set(this.uploadLoading, farm.id, false)
        }
      },
      hasDocs(farm) {
        return getFarmMitigationFiles(farm).length > 0
      },
      getAllDocs(farm) {
        return getFarmMitigationFiles(farm)
      },
      getDocsBySource(farm, sourceType) {
        return filterMitigationFilesBySource(this.getAllDocs(farm), sourceType)
      },
      getDocCountBySource(farm, sourceType) {
        return countMitigationFilesBySource(this.getAllDocs(farm), sourceType)
      },
      hasUntypedDocs(farm) {
        return hasUntypedMitigationFiles(this.getAllDocs(farm))
      },
      getSourceTypesForFarm(farm) {
        const types = [
          DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE,
          DDS_MITIGATION_SOURCE_TYPE.EUDR_DEFORESTATION,
        ]
        if (this.hasUntypedDocs(farm)) types.push('other')
        return types
      },
      getFarmSourceTabIndex(farm) {
        const index = Number(this.farmSourceTabs[farm.id])
        const maxIndex = this.getSourceTypesForFarm(farm).length - 1
        if (Number.isNaN(index) || index < 0 || index > maxIndex) return 0
        return index
      },
      getActiveSourceType(farm) {
        const types = this.getSourceTypesForFarm(farm)
        return types[this.getFarmSourceTabIndex(farm)] || types[0]
      },
      getActiveSourceDocs(farm) {
        return this.getDocsBySource(farm, this.getActiveSourceType(farm))
      },
      getSourceLabel(sourceType) {
        if (sourceType === DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE) {
          return this.$t('dueDiligence.localDbMitigationDocuments')
        }
        if (sourceType === DDS_MITIGATION_SOURCE_TYPE.EUDR_DEFORESTATION) {
          return this.$t('dueDiligence.eudrMitigationDocuments')
        }
        return this.$t('dueDiligence.otherMitigationDocuments')
      },
      isRevisionRequested(farm) {
        return this.localRejected[farm.id] || isMitigationRevisionRequested(farm)
      },
      isFarmApproved(farm) {
        if (this.localApproved[farm.id]) return true
        if (isMitigationEvidenceApproved(farm)) return true
        const info = getProductionPlaceDeforestationInfo(farm)
        const status = (
          info?.deforestationStatus
          || farm.eudr_deforestation_status
          || farm.overallHistoryStatus
          || ''
        ).toLowerCase()
        return status === 'manually mitigated' || status === 'manually_mitigated'
          || status === 'non-deforestation' || status === 'compliant'
      },
      isHighRiskFarm(farm) {
        return isFarmHighRisk(farm)
      },
      isMediumRiskFarm(farm) {
        return isFarmMediumRisk(farm)
      },
      getFarmCardClass(farm) {
        if (this.isFarmApproved(farm)) return 'mep-farm-card--approved'
        if (this.isHighRiskFarm(farm)) return 'mep-farm-card--red'
        return 'mep-farm-card--yellow'
      },
      getFarmAccentClass(farm) {
        if (this.isFarmApproved(farm)) return 'mep-farm-card__accent--green'
        if (this.isHighRiskFarm(farm)) return 'mep-farm-card__accent--red'
        return 'mep-farm-card__accent--yellow'
      },
      docName(doc) {
        const path = doc.file_path || doc.riskMitigationFile || ''
        return path.split('/').at(-1) || 'document.pdf'
      },
      getDocIcon(doc) {
        const ext = (this.docName(doc) || '').split('.').pop().toLowerCase()
        if (ext === 'pdf') return 'mdi-file-pdf-box'
        if (['jpg', 'jpeg', 'png'].includes(ext)) return 'mdi-file-image'
        if (['doc', 'docx'].includes(ext)) return 'mdi-file-word'
        return 'mdi-file-outline'
      },
      getDocIconColor(doc) {
        const ext = (this.docName(doc) || '').split('.').pop().toLowerCase()
        if (ext === 'pdf') return '#DA3D3D'
        if (['jpg', 'jpeg', 'png'].includes(ext)) return '#1976D2'
        if (['doc', 'docx'].includes(ext)) return '#1565C0'
        return '#757575'
      },
      async openDoc(doc) {
        try {
          const path = doc.file_path || doc.riskMitigationFile || ''
          const key = path.split('.com/')[1] || path
          const url = await s3UploadService.getFileUrl(key)
          window.open(url, '_blank')
        } catch (e) {
          console.error(e)
        }
      },
      toggleFarm(farmId) {
        const idx = this.selectedFarms.indexOf(farmId)
        if (idx === -1) this.selectedFarms.push(farmId)
        else this.selectedFarms.splice(idx, 1)
      },
      async approveSelected() {
        if (this.selectedFarms.length === 0) return
        const toApprove = this.mitigationFarms.filter(f => this.selectedFarms.includes(f.id))
        this.openApproveDialog(toApprove)
      },
      openApproveDialog(farmOrFarms) {
        const farms = Array.isArray(farmOrFarms) ? farmOrFarms : [farmOrFarms]
        this.approveDialog = {
          open: true,
          farms,
          isTemporaryApproval: false,
          approvalExpirationValue: this.approvalExpirationValue || 30,
          approvalExpirationUnit: (this.approvalExpirationUnit || 'days').toLowerCase(),
          loading: false,
        }
      },
      openBulkApproveDialog() {
        if (this.selectedFarms.length === 0) return
        const toApprove = this.mitigationFarms.filter(f => this.selectedFarms.includes(f.id))
        this.openApproveDialog(toApprove)
      },
      closeApproveDialog() {
        this.approveDialog.open = false
        this.approveDialog.farms = []
        this.approveDialog.loading = false
      },
      buildApprovalPayload(farm) {
        const { isTemporaryApproval, approvalExpirationValue, approvalExpirationUnit } = this.approveDialog
        const farmId = getDdsUpdateFarmId(farm)
        if (!farmId) throw new Error('Missing farm id for mitigation update')
        const approvalStatus = resolveMitigationApprovalStatus(farm)
        return {
          farm_id: [farmId],
          diligence_report_id: parseInt(this.reportId, 10),
          eudr_deforestation_status: approvalStatus,
          eudr_s3_key: '',
          eudr_s3_location: '',
          eudr_comment: isTemporaryApproval
            ? `Temporary mitigation evidence approval for ${approvalExpirationValue} ${approvalExpirationUnit}`
            : 'Mitigation evidence approved',
          isTemporaryApproval,
          approvalExpirationValue: isTemporaryApproval ? parseInt(approvalExpirationValue, 10) : null,
          approvalExpirationUnit: isTemporaryApproval ? approvalExpirationUnit : null,
        }
      },
      async confirmApprove() {
        const farms = this.approveDialog.farms
        if (!farms.length || !this.isApproveExpirationValid) return

        const { isTemporaryApproval, approvalExpirationValue, approvalExpirationUnit } = this.approveDialog
        this.approveDialog.loading = true
        let successCount = 0

        for (const farm of farms) {
          try {
            const res = await DeforestationService.updateDeforestationStatus(this.buildApprovalPayload(farm))
            throwOnGraphqlErrors(res, 'Failed to approve mitigation evidence')
            this.$set(this.localApproved, farm.id, true)
            this.$delete(this.localRejected, farm.id)
            this.$emit('farm-approved', farm)
            successCount++
          } catch (e) {
            console.error(e)
            this.$notify({
              text: e?.message || 'Failed to approve. Please try again.',
              type: 'error',
            })
          }
        }

        this.selectedFarms = []
        this.approveDialog.loading = false
        this.closeApproveDialog()

        if (successCount > 0) {
          const farmLabel = farms[0].farm?.farmName || 'farm'
          const msg = isTemporaryApproval
            ? (successCount === 1
              ? `Evidence approved for ${farmLabel} (Manually Mitigated for ${approvalExpirationValue} ${approvalExpirationUnit}).`
              : `${successCount} farm(s) approved as Manually Mitigated with temporary period.`)
            : (successCount === 1
              ? `Evidence permanently approved for ${farmLabel} as Manually Mitigated.`
              : `${successCount} farm(s) permanently approved as Manually Mitigated.`)
          this.$notify({ text: msg, type: 'success' })
        } else {
          this.$notify({ text: 'Failed to approve. Please try again.', type: 'error' })
        }
      },
      startReject(farm) {
        this.rejectingFarmId = farm.id
        if (!this.rejectComments[farm.id]) this.$set(this.rejectComments, farm.id, '')
      },
      async approveFarm(farm) {
        this.openApproveDialog(farm)
      },
      async confirmReject(farm) {
        this.$set(this.rejectLoading, farm.id, true)
        try {
          const comment = (this.rejectComments[farm.id] || '').trim()
            || 'Evidence rejected. Please resubmit updated documents.'

          const reportPlaceId = getReportProductionPlaceId(farm)

          if (reportPlaceId) {
            try {
              const persistRes = await DeforestationService.productionPlaceRiskMitigation({
                id: reportPlaceId,
                eudr_deforestation_status: getProductionPlaceDeforestationStatus(farm),
                risk_mitigation_comment: `Revision requested: ${comment}`,
                files: [],
              })
              throwOnGraphqlErrors(persistRes, 'Failed to save revision comment')
            } catch (persistError) {
              console.warn('Revision notification sent but comment was not saved', persistError)
            }
          }

          this.$notify({
            text: 'Farm added to revision batch. Send accumulated notification via Request Additional Information.',
            type: 'success',
          })

          this.$set(this.localRejected, farm.id, true)
          this.rejectingFarmId = null
          this.$emit('revision-queued', {
            farm,
            comment,
            mitigationSourceType: this.getActiveSourceType(farm),
          })
          this.$emit('farm-rejected', farm)
        } catch (e) {
          console.error(e)
          this.$notify({
            text: e?.message || 'Failed to send revision request. Please try again.',
            type: 'error',
          })
        } finally {
          this.$set(this.rejectLoading, farm.id, false)
        }
      },
    },
  }
  </script>
  
  <style scoped>
  .mep-root {
    border: 1px solid #E0E0E0;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
  }
  .mep-header {
    background: linear-gradient(180deg, #FAFBFC 0%, #F5F7FA 100%);
  }
  .mep-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #FFF8E1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .mep-stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid #E0E0E0;
    background: #fff;
  }
  .mep-stat-pill__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .mep-stat-pill__label {
    color: #616161;
  }
  .mep-stat-pill__count {
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
  }
  .mep-stat-pill--yellow .mep-stat-pill__dot { background: #FFA826; }
  .mep-stat-pill--yellow .mep-stat-pill__count { background: #FFF3E0; color: #E65100; }
  .mep-stat-pill--red .mep-stat-pill__dot { background: #DA3D3D; }
  .mep-stat-pill--red .mep-stat-pill__count { background: #FFEBEE; color: #C62828; }
  .mep-stat-pill--green .mep-stat-pill__count { background: #E8F5E9; color: #2E7D32; }
  
  .mep-filter-bar {
    background: #F8FAFC;
    border-bottom: 1px solid #ECEFF1;
  }
  .mep-filter-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #90A4AE;
    margin-right: 4px;
  }
  .mep-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 14px;
    border-radius: 20px;
    border: 1px solid #CFD8DC;
    background: #fff;
    color: #455A64;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .mep-filter-chip:hover {
    border-color: #90A4AE;
    background: #fff;
  }
  .mep-filter-chip--active {
    border-color: #1565C0;
    background: #E3F2FD;
    color: #0D47A1;
    box-shadow: 0 1px 3px rgba(21, 101, 192, 0.15);
  }
  .mep-filter-chip--yellow.mep-filter-chip--active {
    border-color: #FFA826;
    background: #FFF8E1;
    color: #E65100;
    box-shadow: 0 1px 3px rgba(255, 168, 38, 0.2);
  }
  .mep-filter-chip--red.mep-filter-chip--active {
    border-color: #DA3D3D;
    background: #FFF5F5;
    color: #C62828;
    box-shadow: 0 1px 3px rgba(218, 61, 61, 0.2);
  }
  .mep-filter-chip__count {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.06);
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .mep-empty-filter {
    border: 1px dashed #CFD8DC;
    border-radius: 10px;
    background: #FAFAFA;
  }
  
  .mep-farm-card {
    position: relative;
    display: flex;
    border-radius: 10px !important;
    overflow: hidden;
  }
  .mep-farm-card--yellow {
    border-color: #FFE0B2 !important;
    background: #FFFDFA;
  }
  .mep-farm-card--red {
    border-color: #FFCDD2 !important;
    background: #FFFBFB;
  }
  .mep-farm-card--approved {
    border-color: #C8E6C9 !important;
    background: #FAFFFA;
  }
  .mep-farm-card__accent {
    width: 4px;
    flex-shrink: 0;
  }
  .mep-farm-card__accent--yellow { background: #FFA826; }
  .mep-farm-card__accent--red { background: #DA3D3D; }
  .mep-farm-card__accent--green { background: #489E16; }
  .mep-farm-card__body {
    flex: 1;
    min-width: 0;
  }
  
  /* ── Risk & status chips ──────────────────────────────── */
  .mep-risk-chip,
  .mep-status-chip {
    font-weight: 700 !important;
    font-size: 11px !important;
    letter-spacing: 0.25px !important;
    border-radius: 100px !important;
    height: 24px !important;
    padding: 0 10px !important;
    box-shadow: none !important;
  }
  .mep-risk-chip--yellow {
    background: #FEF3C7 !important;
    color: #92400E !important;
    border: 1.5px solid #F59E0B !important;
  }
  .mep-risk-chip--red {
    background: #FFE4E6 !important;
    color: #9F1239 !important;
    border: 1.5px solid #FDA4AF !important;
  }
  .mep-status-chip--approved {
    background: #DCFCE7 !important;
    color: #166534 !important;
    border: 1.5px solid #86EFAC !important;
  }
  .mep-status-chip--awaiting {
    background: #FEF9C3 !important;
    color: #854D0E !important;
    border: 1.5px solid #FDE047 !important;
  }
  .mep-status-chip--revision {
    background: #FFEDD5 !important;
    color: #9A3412 !important;
    border: 1.5px solid #FDBA74 !important;
  }
  .mep-status-chip--pending {
    background: #DBEAFE !important;
    color: #1E40AF !important;
    border: 1.5px solid #93C5FD !important;
  }
  
  .mep-no-docs {
    border: 1px dashed #E0E0E0;
    border-radius: 8px;
    background: #FAFAFA;
  }
  .mep-doc-tile {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;
  }

  .mep-source-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    background: #fff;
    color: #424242;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover {
      background: #f5f5f5;
    }

    &--active {
      background: #E8F5E9;
      border-color: #287444;
      color: #287444;
      font-weight: 600;
    }
  }

  .mep-source-tabs__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    margin-left: 6px;
    padding: 0 6px;
    border-radius: 9px;
    background: #E3F2FD;
    color: #1565C0;
    font-size: 11px;
    font-weight: 700;
  }

  /* ── Progress bar ──────────────────────────────────── */
  .mep-progress__label {
    color: #546E7A;
  }
  .mep-progress__fraction {
    color: #37474F;
  }

  /* ── Sticky bulk action bar ────────────────────────── */
  .mep-bulk-bar {
    position: sticky;
    top: 64px;
    z-index: 10;
    background: #1565C0;
    padding: 10px 16px;
    box-shadow: 0 2px 8px rgba(21, 101, 192, 0.25);
  }
  .mep-bulk-bar__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
  }
  .mep-bulk-bar__pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
  }
  .mep-bulk-bar__pill--yellow {
    background: rgba(255, 168, 38, 0.2);
    color: #FFD54F;
    border: 1px solid rgba(255, 168, 38, 0.4);
  }
  .mep-bulk-bar__pill--red {
    background: rgba(218, 61, 61, 0.2);
    color: #EF9A9A;
    border: 1px solid rgba(218, 61, 61, 0.35);
  }
  .mep-bulk-bar__approve-btn {
    background: #fff !important;
    color: #1565C0 !important;
    font-weight: 700 !important;
    letter-spacing: 0.02em;
  }

  /* ── Evidence flat document list ───────────────────── */
  .mep-docs-section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 2px;
  }
  .mep-docs-group {
    border: 1px solid #E8EDF2;
    border-radius: 8px;
    overflow: hidden;
    background: #FAFCFF;
  }
  .mep-docs-group__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: #F0F4FA;
    border-bottom: 1px solid #E8EDF2;
  }
  .mep-docs-group__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    background: #BBDEFB;
    color: #1565C0;
  }
  .mep-docs-group__badge--local-db-compliance {
    background: #E8F5E9;
    color: #2E7D32;
  }
  .mep-docs-group__badge--eudr-deforestation {
    background: #E3F2FD;
    color: #1565C0;
  }
  .mep-docs-group__badge--other {
    background: #F3E5F5;
    color: #6A1B9A;
  }
  .mep-docs-group__label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #455A64;
    text-transform: uppercase;
  }
  .mep-doc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #EEF2F7;
  }
  .mep-doc-row:last-child {
    border-bottom: none;
  }
  .mep-doc-row__info {
    flex: 1;
    min-width: 0;
  }
  .mep-doc-row__name {
    font-size: 12px;
    font-weight: 500;
    color: #263238;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mep-doc-row__comment {
    font-size: 11px;
    color: #78909C;
    line-height: 1.3;
  }

  /* ── Action row ─────────────────────────────────────── */
  .mep-action-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #F0F0F0;
  }
  .mep-action-row__secondary {
    opacity: 0.85;
  }
  .mep-action-row__primary {
    min-width: 148px !important;
    font-weight: 600 !important;
    letter-spacing: 0.03em;
  }

  .mep-approve-dialog {
    border-radius: 8px;
    overflow: hidden;
  }
  .mep-approve-dialog__checkbox-label {
    line-height: 1.4;
  }
  .mep-approve-dialog__warning {
    background: #fff4e5;
    border: 1px solid #f5b041;
    border-radius: 6px;
    padding: 12px 14px;
  }
  .mep-approve-dialog__warning-title {
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
  }
  .mep-approve-dialog__warning-text {
    font-size: 13px;
    color: #555;
    line-height: 1.45;
  }
  </style>
  